// Test Accounts
// -------------------

import { useState, useCallback, useEffect, useRef } from "react";

import {
  BUTTON_NEXT,
  BUTTON_PREV,
  EMPTY_STRING,
  MAX_LENGTH_USERNAME,
  MAX_LENGTH_PASSPHRASE,
  MAX_LENGTH_TOTP_SECRET,
} from "@/constants/definitions";
import TestUserClass from "@/models/testUser";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import { validateCredentialField, hasSurroundingWhitespace } from "@/utils/textSanitization";
import { AlertTriangle } from "lucide-react";
import { FiInfo } from "react-icons/fi";
import { Tooltip } from "react-tooltip";

// Hint shown under both accounts' authenticator-secret fields. Every
// test-account field is optional, so the bare "(Optional)" tag was noise;
// this states what the field is for (the account's MFA/TOTP seed) and when
// to leave it blank instead.
const TOTP_SECRET_HINT =
  "Only if this account uses two-factor authentication: paste its TOTP secret " +
  "— the base32 setup key from the authenticator app — so testing can sign in. " +
  "Leave blank if it has none.";

interface TestAccountsProps {
  testUser1?: TestUserClass;
  testUser2?: TestUserClass;
  onComplete: (
    name: string,
    testUser1: TestUserClass | null,
    testUser2: TestUserClass | null,
    updateBackend: boolean
  ) => void;
  // Add-Application wizard only — see the matching note on
  // ProjectProperties for why it's optional.
  onCancel?: () => void;
}

function TestAccounts({ testUser1: testUser1Prop, testUser2: testUser2Prop, onComplete, onCancel }: TestAccountsProps) {
  const initialAccount1 = testUser1Prop || new TestUserClass();
  const initialAccount2 = testUser2Prop || new TestUserClass();

  const [username, setUsername] = useState<string>(initialAccount1.username || "");
  const [password, setPassword] = useState<string>(initialAccount1.password || "");
  const [totpSecret, setTotpSecret] = useState<string>(initialAccount1.totpSecret || "");
  const [usernameAlt, setUsernameAlt] = useState<string>(initialAccount2.username || "");
  const [passwordAlt, setPasswordAlt] = useState<string>(initialAccount2.password || "");
  const [totpSecretAlt, setTotpSecretAlt] = useState<string>(initialAccount2.totpSecret || "");
  const [updateBackend, setUpdateBackend] = useState<boolean>(false);
  // Hard validation errors (control chars / over-length) that block Next.
  const [credentialErrors, setCredentialErrors] = useState<string[]>([]);

  // Track previous props for componentDidUpdate comparison
  const prevTestUser1Ref = useRef<TestUserClass | undefined>(testUser1Prop);
  const prevTestUser2Ref = useRef<TestUserClass | undefined>(testUser2Prop);

  useEffect(() => {
    const prevTestUser1 = prevTestUser1Ref.current;
    const prevTestUser2 = prevTestUser2Ref.current;

    const changed1 =
      (!!testUser1Prop?.username || !!testUser1Prop?.password) &&
      (testUser1Prop?.username !== prevTestUser1?.username ||
      testUser1Prop?.password !== prevTestUser1?.password);

    const changed2 =
      (!!testUser2Prop?.username || !!testUser2Prop?.password) &&
      (testUser2Prop?.username !== prevTestUser2?.username ||
      testUser2Prop?.password !== prevTestUser2?.password);

    if (changed1 || changed2) {
      setUsername(testUser1Prop?.username ?? "");
      setPassword(testUser1Prop?.password ?? "");
      setTotpSecret(testUser1Prop?.totpSecret ?? "");
      setUsernameAlt(testUser2Prop?.username ?? "");
      setPasswordAlt(testUser2Prop?.password ?? "");
      setTotpSecretAlt(testUser2Prop?.totpSecret ?? "");
      setUpdateBackend(false); // don't accidentally mark dirty on load
    }

    prevTestUser1Ref.current = testUser1Prop;
    prevTestUser2Ref.current = testUser2Prop;
  }, [testUser1Prop, testUser2Prop]);

  const onButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const target = event.target as HTMLButtonElement;
    const name = target.name;

    // On forward navigation, validate credentials. We NEVER silently trim or
    // rewrite a credential — a mangled value breaks the scan login in a way
    // the user can't see. Instead we reject control chars / over-length with
    // a clear message and block. See SECURITY_AUDIT_FINDINGS F6.
    if (name === BUTTON_NEXT) {
      const credentialErrorMessages = [
        validateCredentialField("Account 1 username", username, MAX_LENGTH_USERNAME),
        validateCredentialField("Account 1 passphrase", password, MAX_LENGTH_PASSPHRASE),
        validateCredentialField("Account 1 authenticator secret", totpSecret, MAX_LENGTH_TOTP_SECRET),
        validateCredentialField("Account 2 username", usernameAlt, MAX_LENGTH_USERNAME),
        validateCredentialField("Account 2 passphrase", passwordAlt, MAX_LENGTH_PASSPHRASE),
        validateCredentialField("Account 2 authenticator secret", totpSecretAlt, MAX_LENGTH_TOTP_SECRET),
      ].filter((message): message is string => message !== null);

      if (credentialErrorMessages.length > 0) {
        setCredentialErrors(credentialErrorMessages);
        return;
      }
    }
    setCredentialErrors([]);

    let builtTestUser1: TestUserClass | null = null;
    if (username) {
      builtTestUser1 = new TestUserClass();
      // PIN is no longer surfaced in the UI — pass empty string so the
      // backend record stays consistent. The model field remains for
      // back-compat with existing data. Credentials (incl. the TOTP secret)
      // are passed EXACTLY as entered — no silent trim. Any surrounding
      // whitespace is surfaced as a visible warning instead (F6).
      builtTestUser1.setSimpleValues(username, password, EMPTY_STRING, 0, totpSecret);
    }
    let builtTestUser2: TestUserClass | null = null;
    if (usernameAlt) {
      builtTestUser2 = new TestUserClass();
      builtTestUser2.setSimpleValues(usernameAlt, passwordAlt, EMPTY_STRING, 1, totpSecretAlt);
    }
    onComplete(name, builtTestUser1, builtTestUser2, updateBackend);
  }, [username, password, totpSecret, usernameAlt, passwordAlt, totpSecretAlt, updateBackend, onComplete]);

  const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    switch (name) {
      case "username":
        setUsername(value);
        break;
      case "password":
        setPassword(value);
        break;
      case "totpSecret":
        setTotpSecret(value);
        break;
      case "usernameAlt":
        setUsernameAlt(value);
        break;
      case "passwordAlt":
        setPasswordAlt(value);
        break;
      case "totpSecretAlt":
        setTotpSecretAlt(value);
        break;
    }
    setUpdateBackend(true);
  }, []);

  // Fields whose value has leading/trailing whitespace. Surfaced as a
  // visible warning rather than silently trimmed, so a credential that will
  // be saved with surrounding spaces is never a hidden surprise (F6).
  const whitespaceWarningFields = [
    { label: "Account 1 username", value: username },
    { label: "Account 1 passphrase", value: password },
    { label: "Account 1 authenticator secret", value: totpSecret },
    { label: "Account 2 username", value: usernameAlt },
    { label: "Account 2 passphrase", value: passwordAlt },
    { label: "Account 2 authenticator secret", value: totpSecretAlt },
  ]
    .filter((field) => hasSurroundingWhitespace(field.value))
    .map((field) => field.label);

  return (
    <>
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-2 gap-6">
          {/* Test Account 1 */}
          <div className="flex flex-col gap-4">
            <p className="flex items-center gap-2 text-sm font-medium text-gray-700">
              Test Account 1
              <FiInfo data-tooltip-id="test-account-1-tooltip" className="text-gray-400" />
            </p>
            <FormField label="Username" htmlFor="username">
              <Input
                type="text"
                name="username"
                id="username"
                value={username}
                maxLength={MAX_LENGTH_USERNAME}
                onChange={onInputChange}
              />
            </FormField>
            <FormField label="Passphrase" htmlFor="password">
              <Input
                type="password"
                name="password"
                id="password"
                value={password}
                maxLength={MAX_LENGTH_PASSPHRASE}
                onChange={onInputChange}
              />
            </FormField>
            <FormField label="MFA authenticator secret" htmlFor="totpSecret">
              <Input
                type="password"
                name="totpSecret"
                id="totpSecret"
                value={totpSecret}
                maxLength={MAX_LENGTH_TOTP_SECRET}
                onChange={onInputChange}
              />
              <p className="mt-1 text-xs text-gray-400">{TOTP_SECRET_HINT}</p>
            </FormField>
          </div>

          {/* Test Account 2 */}
          <div className="flex flex-col gap-4">
            <p className="flex items-center gap-2 text-sm font-medium text-gray-700">
              Test Account 2
              <span className="text-xs font-normal text-gray-400">(Optional)</span>
              <FiInfo data-tooltip-id="test-account-2-tooltip" className="text-gray-400" />
            </p>
            <FormField label="Username" htmlFor="usernameAlt">
              <Input
                type="text"
                name="usernameAlt"
                id="usernameAlt"
                value={usernameAlt}
                maxLength={MAX_LENGTH_USERNAME}
                onChange={onInputChange}
              />
            </FormField>
            <FormField label="Passphrase" htmlFor="passwordAlt">
              <Input
                type="password"
                name="passwordAlt"
                id="passwordAlt"
                value={passwordAlt}
                maxLength={MAX_LENGTH_PASSPHRASE}
                onChange={onInputChange}
              />
            </FormField>
            <FormField label="MFA authenticator secret" htmlFor="totpSecretAlt">
              <Input
                type="password"
                name="totpSecretAlt"
                id="totpSecretAlt"
                value={totpSecretAlt}
                maxLength={MAX_LENGTH_TOTP_SECRET}
                onChange={onInputChange}
              />
              <p className="mt-1 text-xs text-gray-400">{TOTP_SECRET_HINT}</p>
            </FormField>
          </div>
        </div>

        {/* Credential validation errors — control characters / over-length.
            Blocks Next. Credentials are never silently altered (F6). */}
        {credentialErrors.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-red-500" />
            <ul className="list-disc pl-4 space-y-0.5">
              {credentialErrors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Leading/trailing-whitespace warning — surfaced (not silently
            trimmed) so a credential saved with surrounding spaces is
            visible to the user (F6). Non-blocking. */}
        {whitespaceWarningFields.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
            <p>
              These fields have leading or trailing spaces and will be saved
              exactly as entered: {whitespaceWarningFields.join(", ")}. Remove
              the spaces if they were not intentional — they are part of the
              credential and will be used when signing in.
            </p>
          </div>
        )}

        {/* Duplicate-username warning — the BE collapses identical
            test accounts down to a single account at test time, so
            cross-account checks (the reason for offering two accounts
            in the first place) silently won't run. Warn rather than
            block — the user may want a single-account test, in which
            case Account 2 should be left blank. Trim + exact case
            match mirrors the BE comparison. */}
        {username.trim().length > 0 &&
          username.trim() === usernameAlt.trim() && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-500" />
              <p>
                Both test accounts share the same username — only a single
                account will be used in testing. Use a different username for
                Account 2 to enable cross-account checks, or leave Account 2
                blank.
              </p>
            </div>
          )}

        <div className={`flex items-center pt-4 border-t border-gray-100 ${onCancel ? "justify-between" : "justify-end"}`}>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 cursor-pointer"
            >
              Cancel
            </button>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              name={BUTTON_PREV}
              onClick={onButtonClick}
              className="inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 cursor-pointer"
            >
              Previous
            </button>
            <button
              type="button"
              name={BUTTON_NEXT}
              onClick={onButtonClick}
              className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 cursor-pointer"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      <Tooltip
        style={{
          width: 200,
        }}
        id="test-account-1-tooltip"
        place="bottom"
        content="Required for authenticated testing. This account simulates a real user and is essential for discovering vulnerabilities behind login, such as broken access controls or logic flaws."
      />
      <Tooltip
        style={{
          width: 200,
        }}
        id="test-account-2-tooltip"
        place="bottom"
        content="Used for advanced testing techniques involving differential behavior between users. Helps uncover subtle security issues that emerge only when comparing responses between accounts."
      />
    </>
  );
}

export default TestAccounts;
